import { ANDROID_PACKAGE_NAME, APP_STORE_ID } from './appStores';

/**
 * ブランドアプリのストアURLを組み立てる。
 *
 * **サーバ（`storeRedirect.ts`）とVueページ（`coupon-transfer`）の両方から使う。**
 * かつては両者が別々に組み立てており、`/jp/` を入れ忘れた不具合を
 * 3箇所に分けて直すことになった。組み立ては必ずここに集約すること。
 */

/**
 * 生成ファイル（`appStores.ts`）からの引き当て。
 *
 * **`map[appName]` を直接使わないこと。** `appName` はURL由来のユーザー入力で、
 * `__proto__` や `constructor` を渡すと `Object.prototype` 上の値が引けてしまい、
 * `https://apps.apple.com/jp/app/id[object Object]` のような転送先が組み上がる。
 * 自前のキーだけを見るために hasOwnProperty で確かめる。
 */
export function lookupAppValue(map: Readonly<Record<string, string>>, appName: string): string | null {
  return Object.prototype.hasOwnProperty.call(map, appName) ? map[appName] || null : null;
}

/**
 * App Store の URL。
 *
 * **国コード `/jp/` は必ず入れる。** 対象アプリはいずれも日本のみの配信設定のため、
 * 国コードを省くとAppleがアクセス元のIPを見てストアフロントを決めてしまい、
 * **渡航中の日本ユーザー**（Apple アカウントは日本だがIPが海外）が
 * 「この App は お住まいの国では利用できません」に落ちる。
 *
 * ストアフロントはApple アカウントの国・地域で決まり、端末の言語設定とは無関係。
 * 日本以外のアカウントの利用者はそもそもインストールできないので、
 * 国コードを省いても救えない。省く利点が無く、上記の実害だけが残る。
 */
export function buildIosStoreUrl(appName: string): string | null {
  const appStoreId = lookupAppValue(APP_STORE_ID, appName);
  return appStoreId ? `https://apps.apple.com/jp/app/id${appStoreId}` : null;
}

/** Androidのパッケージ名。`intent://` の組み立てなど、URL以外の用途で使う */
export function getAndroidPackageName(appName: string): string | null {
  return lookupAppValue(ANDROID_PACKAGE_NAME, appName);
}

/** Google Play の URL。Playは配信国をアカウント側で解決するため、国の指定は要らない */
export function buildAndroidStoreUrl(appName: string): string | null {
  const packageName = getAndroidPackageName(appName);
  return packageName ? `https://play.google.com/store/apps/details?id=${packageName}` : null;
}

/**
 * 生成ファイルに存在するアプリか。
 * 「アプリ名が間違っている」のか「そのOS向けの配信が無い」のかを見分けるために使う。
 */
export function isKnownApp(appName: string): boolean {
  return Boolean(lookupAppValue(APP_STORE_ID, appName) || lookupAppValue(ANDROID_PACKAGE_NAME, appName));
}
